import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { api } from '../lib/api';
import { ADMIN_HOME } from '../lib/links';
import { useAuth } from '../lib/auth';
import { usePublicConfig } from '../lib/config';
import { AppearanceMenu } from '../lib/appearance';
import { useImportLegacyGuides } from '../lib/tutorials';
import { LinkButton } from './kit';
import { Button, Crest } from './ui';

function LegacyGuideImport() {
  useImportLegacyGuides();
  return null;
}

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const config = usePublicConfig();
  const { productName, appMode } = config;
  const accessMode = config.data?.accessMode ?? (appMode === 'waitlist' ? 'beta' : 'public');
  const reservations = config.data?.reservationsEnabled ?? appMode === 'waitlist';
  const inbox = useQuery({
    queryKey: ['inbox', 'all'],
    queryFn: () => api<{ unread: number }>('/api/inbox'),
    enabled: !!user,
    refetchInterval: 60_000,
    retry: false,
  });
  const unread = inbox.data?.unread ?? 0;
  const navClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-3 py-2 text-sm transition ${isActive ? 'text-gold-bright' : 'text-silver hover:text-paper'}`;

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-gold focus:px-3 focus:py-2 focus:text-ink"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-40 border-b border-overlay/5 bg-navy-950/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" aria-label={`${productName} home`}>
            <Crest />
            <span className="font-display text-xl tracking-tight">{productName}</span>
          </Link>
          <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
            {user ? (
              <>
                <NavLink to="/dashboard" className={navClass}>
                  Dashboard
                </NavLink>
                <NavLink to="/leagues" end className={navClass}>
                  My leagues
                </NavLink>
                <NavLink to="/inbox" className={navClass}>
                  Inbox
                  {unread ? (
                    <span className="ml-1.5 rounded-full bg-gold px-1.5 text-xs font-bold text-ink">
                      {unread}
                    </span>
                  ) : null}
                </NavLink>
                <NavLink to="/help" className={navClass}>
                  Help
                </NavLink>
              </>
            ) : (
              <>
                <a
                  href="/#how"
                  className="rounded-md px-3 py-2 text-sm text-silver hover:text-paper"
                >
                  How it works
                </a>
                <a
                  href="/#sports"
                  className="rounded-md px-3 py-2 text-sm text-silver hover:text-paper"
                >
                  Sports
                </a>
                <NavLink to="/help" className={navClass}>
                  How to play
                </NavLink>
              </>
            )}
          </nav>
          <div className="flex items-center gap-2">
            <AppearanceMenu />
            {user ? (
              <>
                {user.adminRole ? (
                  <NavLink to={ADMIN_HOME} className={navClass} aria-label="Admin dashboard">
                    Admin
                  </NavLink>
                ) : null}
                <NavLink to="/achievements" className={navClass}>
                  <span className="hidden lg:inline">Achievements</span>
                  <span className="lg:hidden" aria-label="Achievements">
                    🏆
                  </span>
                </NavLink>
                <NavLink to="/settings" className={navClass}>
                  <span className="hidden sm:inline">{user.displayName}</span>
                  <span className="sm:hidden">Settings</span>
                </NavLink>
                <Button variant="ghost" onClick={() => void logout()}>
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="whitespace-nowrap px-2 py-2 text-sm text-silver hover:text-paper sm:px-3"
                >
                  Sign in
                </Link>
                {accessMode === 'public' ? (
                  <LinkButton to="/register" className="whitespace-nowrap">
                    <span className="sm:hidden">Join</span>
                    <span className="hidden sm:inline">Become an Owner</span>
                  </LinkButton>
                ) : reservations ? (
                  <LinkButton to="/waitlist" className="whitespace-nowrap">
                    <span className="sm:hidden">Join</span>
                    <span className="hidden sm:inline">Reserve a seat</span>
                  </LinkButton>
                ) : (
                  <LinkButton to="/register" className="whitespace-nowrap">
                    <span className="sm:hidden">Join</span>
                    <span className="hidden sm:inline">Join with an invite</span>
                  </LinkButton>
                )}
              </>
            )}
          </div>
        </div>
      </header>
      {user ? (
        <nav
          aria-label="Primary mobile"
          className="flex justify-around border-b border-overlay/5 bg-navy-950/90 px-2 py-2 text-sm md:hidden"
        >
          <NavLink to="/dashboard" className={navClass}>
            Dashboard
          </NavLink>
          <NavLink to="/leagues" end className={navClass}>
            Leagues
          </NavLink>
          <NavLink to="/inbox" className={navClass}>
            Inbox{unread ? ` (${unread})` : ''}
          </NavLink>
          <NavLink to="/help" className={navClass}>
            Help
          </NavLink>
        </nav>
      ) : null}
      {user ? <LegacyGuideImport /> : null}
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-overlay/5 py-8 text-center text-xs text-slate">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4">
          <nav
            aria-label="Footer"
            className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm"
          >
            <Link to="/help" className="text-silver hover:text-paper">
              How to play
            </Link>
            {user ? (
              <Link
                to="/feedback"
                state={{ from: location.pathname }}
                className="text-silver hover:text-paper"
              >
                Send feedback
              </Link>
            ) : null}
            <Link to="/privacy" className="text-silver hover:text-paper">
              Privacy
            </Link>
            <Link to="/terms" className="text-silver hover:text-paper">
              Beta terms
            </Link>
          </nav>
          <p>
            {productName} is in {accessMode === 'public' ? 'Open Public Beta' : 'private beta'}. A
            work of fiction: all leagues, franchises and athletes are made up, and all money is play
            money.
          </p>
        </div>
      </footer>
    </div>
  );
}
